import { motion } from 'framer-motion';
import { format } from 'date-fns';
import { Clock, Lock, Globe, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { MOOD_OPTIONS, type MoodEntry } from '@/types/mood';
import { useLanguage } from '@/i18n/LanguageContext';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';

interface MoodHistoryProps {
  entries: MoodEntry[];
  onDelete: (id: string) => Promise<void>;
  isLoading?: boolean;
}

export function MoodHistory({ entries, onDelete, isLoading }: MoodHistoryProps) {
  const { language } = useLanguage();

  const content = {
    en: {
      title: 'Your Mood Journey',
      empty: 'No mood entries yet. Start by checking in above!',
      deleteConfirm: 'Delete this mood entry?',
      deleteDesc: 'This action cannot be undone.',
      cancel: 'Cancel',
      delete: 'Delete',
    },
    es: {
      title: 'Tu Viaje Emocional',
      empty: 'Sin entradas aún. ¡Comienza registrando cómo te sientes!',
      deleteConfirm: '¿Eliminar esta entrada?',
      deleteDesc: 'Esta acción no se puede deshacer.',
      cancel: 'Cancelar',
      delete: 'Eliminar',
    },
  };

  const t = content[language as keyof typeof content] || content.en;

  const getMoodOption = (value: number) => {
    return MOOD_OPTIONS.find((m) => m.value === value) || MOOD_OPTIONS[2];
  };

  if (isLoading) {
    return (
      <div className="space-y-3">
        {[1, 2, 3].map((i) => (
          <div
            key={i}
            className="h-16 rounded-lg bg-muted/50 animate-pulse"
          />
        ))}
      </div>
    );
  }

  if (entries.length === 0) {
    return (
      <div className="text-center py-8 text-muted-foreground">
        <p>{t.empty}</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <h4 className="font-medium text-foreground flex items-center gap-2">
        <Clock className="h-4 w-4 text-primary" />
        {t.title}
      </h4>

      <div className="space-y-2 max-h-[300px] overflow-y-auto pr-2">
        {entries.map((entry, index) => {
          const mood = getMoodOption(entry.mood_value);
          const entryDate = new Date(entry.created_at);

          return (
            <motion.div
              key={entry.id}
              initial={{ opacity: 0, x: -10 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: index * 0.05, duration: 0.3 }}
              className={cn(
                'p-3 rounded-lg border',
                mood.bgColor,
                'border-border/30'
              )}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <span className="text-xl">{mood.emoji}</span>
                  <div>
                    <p className={cn('font-medium text-sm', mood.color)}>
                      {mood.label}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {format(entryDate, 'MMM d, yyyy')} at {format(entryDate, 'h:mm a')}
                    </p>
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  {entry.is_public ? (
                    <Globe className="h-3.5 w-3.5 text-primary" />
                  ) : (
                    <Lock className="h-3.5 w-3.5 text-muted-foreground" />
                  )}

                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7 text-muted-foreground hover:text-destructive"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>{t.deleteConfirm}</AlertDialogTitle>
                        <AlertDialogDescription>{t.deleteDesc}</AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>{t.cancel}</AlertDialogCancel>
                        <AlertDialogAction
                          onClick={() => onDelete(entry.id)}
                          className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                        >
                          {t.delete}
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </div>
              </div>

              {entry.notes && (
                <p className="mt-2 text-xs text-muted-foreground italic pl-9 line-clamp-2">
                  "{entry.notes}"
                </p>
              )}
            </motion.div>
          );
        })}
      </div>
    </div>
  );
}
